import React from "react";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { SiTicktick } from "react-icons/si";
import { Button } from "../../../../components/ui";

/**
 * SMSResponseModal component displays a modal with the response of an SMS operation.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.response - The response object containing message, error, success, and errors.
 * @param {string} [props.response.message] - The success message.
 * @param {string} [props.response.error] - The error message.
 * @param {string} [props.response.capture_error] - Board archive failure, when the texts still ran.
 * @param {Array<string>} props.response.success - The list of successful SMS operations.
 * @param {Array<Object>} [props.response.errors] - The list of errors occurred during SMS operations.
 * @param {Object} props.response.errors[].worker - The worker object associated with the error.
 * @param {string} props.response.errors[].worker.first - The first name of the worker.
 * @param {string} props.response.errors[].worker.last - The last name of the worker.
 * @param {string} props.response.errors[].status - The status of the error.
 * @param {string} props.response.errors[].message - The error message.
 * @param {Function} props.onClose - The function to call when the modal is closed.
 * @returns {JSX.Element} The rendered SMSResponseModal component.
 */
const SMSResponseModal = ({ response, onClose, isOpen, setIsOpen }) => {
  /**
   * Hide the SMS result modal and notify the parent.
   *
   * @param {void}
   * @returns {void}
   */
  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed w-full m-auto inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="flex flex-col bg-white p-4 w-100 max-w-96 rounded-md shadow-lg">
        {response.message ? (
          <SiTicktick size={64} className="text-green-600 m-auto" />
        ) : (
          <AiOutlineExclamationCircle
            size={64}
            className="text-red-600 m-auto"
          />
        )}
        <p className="my-4 text-xs text-gray-500 text-bold m-auto">
          {response.message || response.error}
        </p>
        {response.capture_error ? (
          <p className="mb-3 text-xs text-red-600">
            The board archive did not finish: {response.capture_error}
          </p>
        ) : null}
        <div className="text-xs mb-4">
          <div>
            {(response.success || []).map((success, index) => (
              <div
                key={index}
                className="flex flex-col text-2xs bg-green-200 py-[2px] px-1 rounded mb-1 w-full"
              >
                <div className="flex flex-row justify-between">
                  <b>{success}</b>
                  <i className="ml-auto">Sent</i>
                </div>
              </div>
            ))}
          </div>
          {response.errors && response.errors.length > 0 && (
            <div>
              {response.errors.map((err, index) => (
                <div
                  key={index}
                  className="flex flex-col text-2xs bg-red-200 py-[2px] px-1 rounded mb-1 w-full"
                >
                  <div className="flex flex-row justify-between">
                    <b>
                      {err.worker
                        ? `${err.worker.first || ""} ${err.worker.last || ""}`.trim()
                        : "SMS"}
                    </b>
                    <i className="ml-auto">{err.status}</i>
                  </div>
                  <p>{err.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button text="Close" variant="secondary" onClick={handleClose} />
      </div>
    </div>
  );
};

export default SMSResponseModal;
